import type {
  ISelectItem,
  IEntitySelectItem,
  IEntitySelectItemWithOptionalAvatar,
  TEntityId,
  IEntityDataItem,
  TDisplayString,
  IStatusCount,
  IEntityParentObjectForCreate,
} from '../models'
import type { IOverviewLastSelectedState } from '../overview/models'
import type { ICall } from '../call/models'
import type { IActivityEmail } from '../email/models'
import type { IMeeting } from '../meeting/models'
import type { IMSService, ITermBreakdown } from '../msService/models'
import type { ITeamMember } from '../teamMember/models'
import type { TContact, ISupplierContact } from '../contact/models'
import type { IContract } from '../contract/models'
import type { TAggregateTerm } from '../opportunity/models'
import type { IOrderDocument } from '../document/models'
import type {
  IOrderNote,
  IOrderMSNote,
  IOrderMSServiceNote,
} from '../note/models'
import type { ITask } from '~/types/task/models'

// Order Overview

export interface IOrderOverview {
  order: IOrder
  opportunity?: never
  orderMasterServices: IOrderMasterService[]
  orderTeamMembers: IOrderTeamMember[]
  orderContacts: IOrderContact[]
  orderServiceOrders: IOrderServiceOrder[]
  orderDocuments: IOrderDocument[]
  orderNotes: IOrderNote[]
  contracts: IContract[]
  tasks: ITask[]
  calls: ICall[]
  emails: IActivityEmail[]
  meetings: IMeeting[]
  lastSelectedState: IOrderLastSelectedState | undefined
  [key: string]: any
}

// The body of a POST built on an Overview's shape: IOrderOverviewForPost,
// ICompanyOverviewForPost. A body that is simply not an entity is
// INonEntityForPost (types/models.ts).
export interface IOverviewForPost {}

export interface IOrderOverviewForPost extends IOverviewForPost {
  order: Partial<IOrder>
  opportunity?: never
  orderMasterServices: Partial<IOrderMasterService>[]
  orderTeamMembers?: IOrderTeamMember[]
  orderContacts?: IOrderContact[]
  [key: string]: any
}

export interface IOrder extends IEntityDataItem {
  descr: string
  opportunity?: IEntitySelectItem
  company: IEntitySelectItemWithOptionalAvatar
  orderType: ISelectItem[]
  suppliers?: IEntitySelectItem[]
  supplierDescr?: TDisplayString
  lastUpdatedDate?: TDisplayString
  recurs?: string
  paid?: string
  mrcAmount?: number
  nrcAmount?: number
  oneTimeAmount?: number
  tcvAmount?: number
  owner?: IEntitySelectItem
  assignedTo?: IEntitySelectItem
  defaultProject?: IEntitySelectItem
  numMasterServices: number
  numOrderMSServices: number
  numContracts: number
  numOrderServiceOrders: number
  momssStatusCounts: IStatusCount[]
  contractStatusCounts: IStatusCount[]
  orderServiceOrderStatusCounts: IStatusCount[]
  numDaysSinceCreation?: number
  status: ISelectItem
  [key: string]: any
}

export interface IOrderMasterService extends IEntityDataItem {
  descr: string
  order: IEntitySelectItem
  opportunityMasterService?: IEntitySelectItem
  companyMasterService?: IEntitySelectItem
  origin: ISelectItem
  term?: TAggregateTerm // from msServices
  termBreakdown?: ITermBreakdown // set when the msServices' terms vary
  numLocations: number
  numServices: number
  momssStatusCounts: IStatusCount[]
  project?: IEntitySelectItem
  status: ISelectItem
  orderMSServices: IOrderMSService[]
  orderMSContacts: IOrderMSContact[]
  orderMSTeamMembers: IOrderMSTeamMember[]
  orderMSNotes?: IOrderMSNote[]
}

export interface IOrderMSService extends IMSService {
  opportunityMSService?: IEntitySelectItem
  type?: ISelectItem
  osomsFieldSetData?: string
  osomsTargetDate?: string
  osomsAcceptedOnDate?: string
  osomsCompletedOnDate?: string
  lastUpdatedDate?: string
  orderMSServiceNotes?: IOrderMSServiceNote[]
}

export interface IOrderServiceOrder extends IEntityDataItem {
  order: IEntitySelectItem
  descr: string
  supplier: IEntitySelectItemWithOptionalAvatar
  supplierOrderNum: string
  supplierProductionManager?: ISupplierContact
  tenantProductionManager?: ITeamMember
  orderType: ISelectItem[]
  enteredBy: ITeamMember
  doExpedite: boolean
  expediteFeeAmount: number | null
  numOrderMSServices?: number
  momssStatusCounts: IStatusCount[] // This property and the three below get
  numDaysSinceCreation?: number // calculated client-side in real-time
  doesExceedNumDaysToLateRed?: boolean
  doesExceedNumDaysToLateAmber?: boolean
  status: ISelectItem
}

// Order TeamMember

interface IOrderTeamMemberBase extends IEntityDataItem {
  label?: string
  teamMember: ITeamMember
  splitFraction: number | null
  isOwner?: boolean
  isAssignedTo?: boolean
  status: ISelectItem
  [key: string]: any
}

export interface IOrderTeamMember extends IOrderTeamMemberBase {
  order: IEntitySelectItem
}

export interface IOrderMSTeamMember extends IOrderTeamMemberBase {
  orderMasterService: IEntitySelectItem
}

type TMSLastSelectedChild = [TEntityId, TEntityId]

// export interface IOrderLastSelectedState
//   extends IOverviewLastSelectedState {
//   orderId: TEntityId
//   selectedMOMSId: TEntityId | undefined
//   selectedOrderServiceOrderId: TEntityId | undefined
//   selectedOrderMSServiceIds: TMSLastSelectedChild[] | undefined
// }

export interface IOrderLastSelectedState extends IOverviewLastSelectedState {
  orderId: TEntityId
  selectedMSId?: TEntityId | null
  /**
   * An OrderServiceOrder id — NOT the owning Order, which is `orderId` above.
   *
   * ⚠ WAS `selectedOrderId` until 2026-09-09, and that was the last survivor of
   * the MasterOrder->Order / Order->ServiceOrder rename: `f375a59d` (2024-10-23)
   * wrote the stale name here while commenting out a block directly above that
   * already had the right one. The API mirrored the webapp's mistake, then fixed
   * it in WXTG/cynque-api-server#1547 (closing #1543) — its column is now
   * `selected_order_service_order_id`, which `alias_generator=to_camel` exposes
   * as exactly this name.
   *
   * 🔑 The WRITE was always correct (`SiloOverviewOrderMixin` has sent
   * `selectedOrderServiceOrderId` since before `bridge-final`); only the type,
   * the read-back and the mock carried the old name — so the value was being
   * stored and then never read.
   */
  selectedOrderServiceOrderId?: TEntityId | null
  /**
   * ⚠ SENT, BUT NOT STORED — the Order model has no `selected_contract_id`
   * column (Company and Opportunity both do). Declared here because the webapp
   * genuinely has an Order contract selector and `selectedContractIdDidChange`
   * sends this on every change; pydantic's `extra='ignore'` then discards it.
   * Filed as WXTG/cynque-api-server#1593. ⚠ The MSW mock DOES persist it, so a
   * local reload restores a contract that production would lose.
   */
  selectedContractId?: TEntityId | null
  selectedMSServiceIds?: TMSLastSelectedChild[] | null
}

// Order Contact

interface IOrderContactBase extends IEntityDataItem {
  label?: string
  contact: TContact
  splitFraction: number | null
  status: ISelectItem
  [key: string]: any
}

export interface IOrderContact extends IOrderContactBase {
  order: IEntitySelectItem
}

export interface IOrderMSContact extends IOrderContactBase {
  orderMasterService: IEntitySelectItem
}

// Parent Objects for Create

export interface IOrderParentObjectForCreate {
  entityName: 'order'
  object: IEntitySelectItem
  replyAttachableDocuments?: IEntityParentObjectForCreate['replyAttachableDocuments']
}

export interface IOrderMasterServiceParentObjectForCreate {
  entityName: 'orderMasterService'
  object: IEntitySelectItem
  [key: string]: any
}

export interface IOrderMSServiceParentObjectForCreate {
  entityName: 'orderMSService'
  object: IEntitySelectItem
}

export function isOrder(val: unknown): val is IOrder {
  return (val as IOrder).hasOwnProperty('orderType')
}
